import React, { createContext, useContext, useMemo } from 'react';

import { useFieldPresence } from './useFieldPresenceStore';

// P12 (D30) — field-level editing presence, the FORM-side half (the store's other half is the
// Diagrams PresenceLayer writer). Lives in Common so shared form primitives can consume it
// without a feature dependency. Gives a form three things, all no-ops when no editor context is
// mounted (so a form rendered outside a live diagram behaves normally):
//   • FieldPresenceProvider — mounted per open editor; supplies the element target ({targetType,
//     targetId}) + the publish callbacks (wired to sendEditing up in DiagramEditor).
//   • useFieldEditing(fieldKey) — returns {onFocus,onChange,onBlur} to attach to an input so this
//     user PUBLISHES which field they're on (focus → faint, change → active/intense, blur → clear).
//   • FieldPresenceRing — wraps an input to RENDER a peer's ring + "✎ name" when they're on this field.
//
// Advisory only (D30): never disables the input, never blocks a keystroke, never persists (D10).

const noop = () => {};
const EMPTY = {
  // `active` = a live editor context is mounted (a diagram co-edit session). False here means no
  // provider, so the wrappers below short-circuit to bare children — these renderers are app-wide.
  active: false,
  targetType: null,
  targetId: null,
  publishFocus: noop,
  publishChange: noop,
  publishBlur: noop,
};

const FieldPresenceContext = createContext(EMPTY);

// Mounted around an open editor's form tree. `target` = { targetType:'node'|'edge', targetId } of the
// element being edited (null when nothing is open). The publish* callbacks are stable (defined in
// DiagramEditor, reading refs), so the context value only changes identity when the target changes.
export function FieldPresenceProvider({
  target,
  publishFocus,
  publishChange,
  publishBlur,
  children,
}) {
  const value = useMemo(
    () => ({
      active: true,
      targetType: target?.targetType ?? null,
      targetId: target?.targetId ?? null,
      publishFocus: publishFocus ?? noop,
      publishChange: publishChange ?? noop,
      publishBlur: publishBlur ?? noop,
    }),
    [target?.targetType, target?.targetId, publishFocus, publishChange, publishBlur],
  );
  return <FieldPresenceContext.Provider value={value}>{children}</FieldPresenceContext.Provider>;
}

// True when a FieldPresenceProvider is mounted (i.e. this form is inside a live diagram editor). The
// wrappers below use it to render children bare in every other form in the app — no wrapper <div>, no
// store-driven chrome. It's fixed for a form's lifetime (the provider wraps the whole editor and mounts
// before the inner forms), so it never flips mid-life and can't remount an input by toggling the wrapper.
function useFieldPresenceActive() {
  return useContext(FieldPresenceContext).active;
}

// Handlers to attach onto an input for a given fieldKey. `onChange` here only PUBLISHES presence — it
// does not replace the form's own onChange; call both. Safe no-op with no provider mounted.
export function useFieldEditing(fieldKey) {
  const ctx = useContext(FieldPresenceContext);
  return useMemo(
    () => ({
      onFocus: () => fieldKey && ctx.publishFocus(fieldKey),
      onChange: () => fieldKey && ctx.publishChange(fieldKey),
      onBlur: () => ctx.publishBlur(),
    }),
    [ctx, fieldKey],
  );
}

// The peer (if any) currently editing `fieldKey` of the open element → { color, name, active } | null.
export function useFieldRing(fieldKey) {
  const { targetType, targetId } = useContext(FieldPresenceContext);
  return useFieldPresence(targetType, targetId, fieldKey);
}

// The peer-color ring + "✎ name" tag chrome, given a resolved ring ({color,name,active}) or null.
function ringDecoration(ring, radius) {
  if (!ring) return { style: null, tag: null };
  const width = ring.active ? 2.5 : 1.5; // focus = faint, actively changing = intense
  return {
    style: {
      borderRadius: radius,
      boxShadow: `0 0 0 ${width}px ${ring.color}`,
      transition: 'box-shadow 0.12s ease',
    },
    tag: (
      <span
        style={{
          position: 'absolute',
          top: -9,
          right: 6,
          padding: '1px 6px',
          fontSize: 10,
          lineHeight: '14px',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          color: '#fff',
          background: ring.color,
          borderRadius: radius,
          pointerEvents: 'none', // advisory — never intercept clicks/focus
          zIndex: 2,
        }}
      >
        ✎ {ring.name}
      </span>
    ),
  };
}

// RENDER wrapper: draws a peer's ring around a field (used by the shared config/RHF field renderers,
// which already put onFocus/onChange/onBlur on their own wrapper — and by FieldPresenceInput below,
// which passes the publish handlers in via `wrapperProps`).
//
// When a provider IS mounted it ALWAYS renders the wrapper (ring present or not), so a ring
// appearing/disappearing never changes the element tree at this position — a conditional wrapper would
// remount the child input and drop the LOCAL user's focus/cursor the moment a peer co-edits the field.
// When NO provider is mounted (every non-diagram form in the app) it renders children bare: no wrapper
// <div>, no store-driven chrome — zero cost and no layout change for forms this feature doesn't touch.
// `active` is fixed per form lifetime, so this branch never flips and never triggers that remount.
export function FieldPresenceRing({
  fieldKey,
  children,
  className,
  style,
  radius = 6,
  wrapperProps,
}) {
  const active = useFieldPresenceActive();
  const ring = useFieldRing(fieldKey);
  if (!active) return children;
  const { style: ringStyle, tag } = ringDecoration(ring, radius);
  return (
    <div
      className={className}
      style={{ position: 'relative', ...(ringStyle || {}), ...style }}
      {...wrapperProps}
    >
      {children}
      {tag}
    </div>
  );
}

// PUBLISH + RENDER wrapper for HAND-RENDERED inputs (bespoke cards, array/table rows) where the field
// has no shared renderer to hook. Delegates the ring rendering to FieldPresenceRing and adds the
// publish handlers to the same wrapper: focus/blur via React's bubbling onFocus/onBlur, and value
// changes via the bubbling native `onInput` — so any descendant native <input> upgrades the peer ring
// to "active" on type without threading a handler into each one.
//
// `onInput` only fires for native inputs, so a react-select / custom widget (whose value change is a
// JS callback, not a DOM input event) would show only the focus ring. For those, pass a FUNCTION child
// — it receives { publishChange } to call inside the widget's own onChange, upgrading the ring to
// "active" on pick. Element children keep the native-onInput path unchanged. Safe in a .map().
export function FieldPresenceInput({ fieldKey, children, className, style, radius = 6 }) {
  const editing = useFieldEditing(fieldKey);
  return (
    <FieldPresenceRing
      fieldKey={fieldKey}
      className={className}
      style={style}
      radius={radius}
      wrapperProps={{ onFocus: editing.onFocus, onBlur: editing.onBlur, onInput: editing.onChange }}
    >
      {typeof children === 'function' ? children({ publishChange: editing.onChange }) : children}
    </FieldPresenceRing>
  );
}
